import {
  type ComponentProps, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { MetricInferenceSourceTag } from '~/_shared/components/metricsList/item/metricInferenceSourceTag.component';
import type { FillConfigMetricInferenceSource } from '~/_shared/utils/metric/metrics.types';
import { notUndefined } from '~/_shared/utils/typeGuards';
import { boundaryTerritoryGroupActivate } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { useBTGsGroupedByBoundaryGroupSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';

type MetricInferenceSourceTagContainerProps = Omit<ComponentProps<typeof MetricInferenceSourceTag>, 'onFillClick' | 'showFillNumbers'>;

export const MetricInferenceSourceTagContainer = ({
  inferenceSource,
  ...restProps
}: MetricInferenceSourceTagContainerProps) => {
  const dispatch = useDispatch();
  const groupedBTGs = useBTGsGroupedByBoundaryGroupSelector();
  const isMapPresentational = useIsMapPresentationalSelector();
  const publicMapSettings = usePublicMapSettingsSelector();
  const isMobileScreen = useIsMobileScreenSelector();
  const firstBoundaryGroupId = inferenceSource?.inferenceSources?.[0]?.btg.boundaryGroupId;
  const BTGs = notUndefined(firstBoundaryGroupId) ?
    groupedBTGs.all.find((btg) => btg.boundaryGroupId === firstBoundaryGroupId) :
    undefined;
  const mapHasMoreThanOneFill = notUndefined(BTGs) ? (BTGs.boundaryTerritoryGroups.length > 1) : false;

  const activateFill = useCallback((source: FillConfigMetricInferenceSource) => {
    dispatch(boundaryTerritoryGroupActivate(source.btg));
    dispatch(boundaryTerritoryGroupHighlightStarted(source.btg.boundaryGroupId));
  }, [dispatch]);

  if (isMobileScreen) {
    // mobileMapToolsAsMenuItems is hardcoded not to offer any of the Boundary tools asn as such it should not be available on mobile.
    return null;
  }

  if (isMapPresentational && !publicMapSettings.boundaryTool) {
    return null;
  }

  return (
    <MetricInferenceSourceTag
      {...restProps}
      inferenceSource={inferenceSource}
      onFillClick={activateFill}
      showFillNumbers={mapHasMoreThanOneFill}
    />
  );
};
